import React, {type ComponentType} from 'react';
import {getSystem, getMeta, TypeImplementation} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {BaseButton as Button} from '@packages/systems/design-system/Button';

import {TBoolean, EBoolean} from '@packages/systems/wfdl-ast';

type System = {
  label: string;
  labelVariant: 'hidden';
};

type Meta = {
  secondaryLabel?: string;
  icon?: ComponentType<any>;
} | null;

const validateTypeInDevelopment = (
  expr: ContextualExpression
): [System, Meta] => {
  const {type} = getAnn(expr);
  if (type === null) {
    throw Error('Expected type from AnnotatedExpression to be defined');
  }
  const system = getSystem(type);
  const meta = getMeta(type);

  if (process.env.NODE_ENV === 'development') {
    const exprValue = ContextualExpressions.getBoolean(stripAnn(expr));
    if (typeof exprValue !== 'boolean') {
      throw Error(
        'Expected exprValue to be a boolean received' + typeof exprValue
      );
    }
    if (system === null) {
      throw Error('Expected system from AnnotatedExpression to be defined');
    }

    if (!system.hasOwnProperty('label')) {
      throw Error('ToggleButton requires a label');
    }

    if (
      !system.hasOwnProperty('labelVariant') ||
      system.labelVariant !== 'hidden'
    ) {
      throw Error('labelVariant is required to be `hidden`');
    }
  }
  return [
    {label: (system && system.label) || '', labelVariant: 'hidden'},
    meta,
  ];
};

const ToggleButton = TypeImplementation.Alias({
  type: TBoolean,
  render: ({expr, dispatch}) => {
    const toggle = (show: boolean) => {
      dispatch(updateExpressionAction(EBoolean(!show)));
    };
    const [system, meta] = validateTypeInDevelopment(expr);
    const exprValue = ContextualExpressions.getBoolean(stripAnn(expr));

    let icon = null;
    let label = system.label;
    if (meta) {
      if (meta.icon) {
        const IconComponent = meta.icon;
        icon = <IconComponent />;
      }
      if (meta.secondaryLabel) {
        label = exprValue ? meta.secondaryLabel : system.label;
      }
    }

    return (
      <div
        data-automation-id="ToggleButtonAlias"
        style={{
          gridColumn: '1 / -1',
        }}
      >
        <Hitarea>
          <Button
            data-automation-id="ToggleButton"
            style={{gap: '4px'}}
            selected={exprValue}
            onClick={() => toggle(exprValue || false)}
          >
            {icon}
            <span>{label}</span>
          </Button>
        </Hitarea>
      </div>
    );
  },
});

export default ToggleButton;
